import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import {
  TrashIcon,
  UserPlusIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

const Deleghe = () => {
  const {
    user,
    activeCongregazione,
    delegheLoading,
    refreshDeleghe,
  } = useAuth();

  const [volontarioIdInput, setVolontarioIdInput] = useState("");
  const [ruoloDelega, setRuoloDelega] = useState("volontario");
  const [note, setNote] = useState("");
  const [loadingDeleghe, setLoadingDeleghe] = useState(false);
  const [deleghe, setDeleghe] = useState([]);
  const [searchDeleghe, setSearchDeleghe] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const canManage =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";

  const fetchDeleghe = useCallback(async () => {
    if (!activeCongregazione?.id) {
      setDeleghe([]);
      return;
    }
    setLoadingDeleghe(true);
    try {
      const response = await api.get(
        `/deleghe?congregazione_id=${activeCongregazione.id}`
      );
      setDeleghe(response.data || []);
    } catch (error) {
      console.error("Errore nel recupero delle deleghe:", error);
      toast.error("Impossibile caricare le deleghe");
    } finally {
      setLoadingDeleghe(false);
    }
  }, [activeCongregazione]);

  useEffect(() => {
    fetchDeleghe();
  }, [fetchDeleghe]);

  const handleAddDeleghe = async (e) => {
    e.preventDefault();
    if (!activeCongregazione?.id) {
      toast.error("Seleziona prima una congregazione attiva");
      return;
    }

    const volontarioId = parseInt(volontarioIdInput, 10);
    if (Number.isNaN(volontarioId)) {
      toast.error("Inserisci un ID proclamatore valido");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/deleghe", {
        volontario_id: volontarioId,
        congregazione_id: activeCongregazione.id,
        ruolo_delega: ruoloDelega,
        note: note?.trim() || null,
      });
      toast.success("Delega aggiunta con successo");
      setVolontarioIdInput("");
      setNote("");
      await fetchDeleghe();
      refreshDeleghe();
    } catch (error) {
      const message =
        error.response?.data?.message || "Errore nella creazione della delega";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveDeleghe = async (id) => {
    setRemovingId(id);
    try {
      await api.delete(`/deleghe/${id}`);
      toast.success("Delega rimossa");
      await fetchDeleghe();
      refreshDeleghe();
    } catch (error) {
      const message =
        error.response?.data?.message || "Errore nella rimozione della delega";
      toast.error(message);
    } finally {
      setRemovingId(null);
    }
  };

  const normalizedSearch = searchDeleghe.trim().toLowerCase();
  const filteredDeleghe = normalizedSearch
    ? deleghe.filter((delega) => {
        const nome = delega.volontario_nome || "";
        const cognome = delega.volontario_cognome || "";
        const email = delega.volontario_email || "";
        const telefono = delega.volontario_telefono || "";
        const idValue = delega.volontario_id ?? "";
        const fullName = `${nome} ${cognome}`.trim().toLowerCase();
        return (
          fullName.includes(normalizedSearch) ||
          email.toLowerCase().includes(normalizedSearch) ||
          telefono.toLowerCase().includes(normalizedSearch) ||
          String(idValue).includes(normalizedSearch)
        );
      })
    : deleghe;

  const exportRows = normalizedSearch ? filteredDeleghe : deleghe;

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (
      stringValue.includes(",") ||
      stringValue.includes('"') ||
      stringValue.includes("\n")
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const handleExportCsv = () => {
    if (exportRows.length === 0) {
      toast.error("Nessuna delega da esportare");
      return;
    }

    setExporting(true);
    try {
      const headers = [
        "delega_id",
        "volontario_id",
        "nome",
        "cognome",
        "congregazione_origine_id",
        "congregazione_origine_codice",
        "congregazione_origine_nome",
        "congregazione_delegata_id",
        "congregazione_delegata_codice",
        "congregazione_delegata_nome",
        "ruolo_delega",
        "email",
        "telefono",
        "coniuge_id",
        "coniuge_nome",
        "coniuge_cognome",
        "coniuge_nome_completo",
        "note",
        "created_at",
        "updated_at",
      ];

      const rows = exportRows.map((delega) => {
        const coniugeNomeCompleto = [
          delega.coniuge_nome || "",
          delega.coniuge_cognome || "",
        ]
          .join(" ")
          .trim();

        return [
          delega.id,
          delega.volontario_id,
          delega.volontario_nome || "",
          delega.volontario_cognome || "",
          delega.volontario_congregazione_origine_id || "",
          delega.volontario_congregazione_origine_codice || "",
          delega.volontario_congregazione_origine_nome || "",
          delega.congregazione_id || "",
          delega.congregazione_codice || "",
          delega.congregazione_nome || "",
          delega.ruolo_delega || "",
          delega.volontario_email || "",
          delega.volontario_telefono || "",
          delega.coniuge_id || "",
          delega.coniuge_nome || "",
          delega.coniuge_cognome || "",
          coniugeNomeCompleto,
          delega.note || "",
          delega.created_at || "",
          delega.updated_at || "",
        ];
      });

      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.map(escapeCsvValue).join(",")),
      ].join("\n");

      const bom = "\ufeff";
      const blob = new Blob([bom + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const datePart = new Date().toISOString().slice(0, 10);
      const congregazioneCode = activeCongregazione?.codice || "sconosciuta";
      link.href = url;
      link.download = `deleghe_${congregazioneCode}_${datePart}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (normalizedSearch) {
        toast.success("CSV esportato (risultati filtrati)");
      } else {
        toast.success("CSV esportato con successo");
      }
    } catch (error) {
      console.error("Errore durante l'export CSV deleghe:", error);
      toast.error("Errore durante l'esportazione CSV");
    } finally {
      setExporting(false);
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex items-center space-x-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Accesso limitato
            </h2>
            <p className="text-sm text-gray-600">
              Solo gli amministratori possono gestire le deleghe dei
              proclamatori.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!activeCongregazione?.id) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl border border-amber-200 p-5 text-amber-800">
          Seleziona una congregazione attiva prima di gestire le deleghe.
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs uppercase tracking-wide text-primary-600 font-semibold">
                Deleghe congregazione attiva
              </p>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeCongregazione.nome}
              </h1>
              <p className="text-sm text-gray-500">
                Codice: {activeCongregazione.codice}
              </p>
            </div>
            <div className="flex flex-wrap sm:justify-end gap-3 text-sm text-gray-600">
              <div className="flex flex-col bg-primary-50 text-primary-700 rounded-xl px-4 py-2 min-w-[140px]">
                <span className="text-xs uppercase tracking-wide">
                  Deleghe attive
                </span>
                <span className="text-xl font-semibold">{deleghe.length}</span>
              </div>
              <div className="flex flex-col bg-indigo-50 text-indigo-700 rounded-xl px-4 py-2 min-w-[140px]">
                <span className="text-xs uppercase tracking-wide">
                  Ruolo utente
                </span>
                <span className="text-base font-semibold">
                  {user?.ruolo === "super_admin" ? "Super admin" : "Admin"}
                </span>
              </div>
            </div>
          </div>
          <div className="accent-line mt-4" />
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
              <UserPlusIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Aggiungi delega
              </h2>
              <p className="text-sm text-gray-500">
                Inserisci l’ID del proclamatore da associare a questa
                congregazione. (Puoi trovare l’ID nella sezione Gestione
                Volontari.)
              </p>
              <div className="accent-line mt-3" />
            </div>
          </div>
          <form
            className="grid gap-4 md:grid-cols-2"
            onSubmit={handleAddDeleghe}
          >
            <div>
              <label className="form-label">ID Proclamatore *</label>
              <input
                type="text"
                className="form-input"
                value={volontarioIdInput}
                onChange={(e) => setVolontarioIdInput(e.target.value)}
                placeholder="Es: 123"
                required
              />
            </div>
            <div>
              <label className="form-label">Ruolo nella congregazione</label>
              <select
                className="form-input"
                value={ruoloDelega}
                onChange={(e) => setRuoloDelega(e.target.value)}
              >
                <option value="volontario">Volontario</option>
                <option value="admin">Amministratore</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Note (opzionale)</label>
              <textarea
                className="form-input"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Annotazioni utili per questo incarico"
              />
            </div>
            <div className="md:col-span-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <p className="text-xs text-gray-500 sm:text-right">
                Ricorda: l’ID è visibile nella sezione Gestione Volontari.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full sm:w-auto px-6"
              >
                {submitting ? "Salvataggio..." : "Aggiungi delega"}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="p-6 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Deleghe attive
              </h2>
              <p className="text-sm text-gray-500">
                Elenco dei proclamatori che possono operare su{" "}
                {activeCongregazione.nome}.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={handleExportCsv}
                disabled={loadingDeleghe || delegheLoading || exporting || exportRows.length === 0}
                className="inline-flex items-center justify-center text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                {exporting ? "Esportazione..." : "Esporta"}
              </button>
              <button
                onClick={() => {
                  fetchDeleghe();
                  refreshDeleghe();
                }}
                disabled={loadingDeleghe || delegheLoading}
                className="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Aggiorna elenco
              </button>
            </div>
          </div>
          <div className="accent-line mx-6" />
          <div className="px-6 pb-4 pt-4">
            <label className="form-label">Cerca delega</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Nome o cognome..."
                className="form-input pl-10"
                value={searchDeleghe}
                onChange={(e) => setSearchDeleghe(e.target.value)}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Risultati: {filteredDeleghe.length}
            </p>
          </div>

          {loadingDeleghe ? (
            <div className="p-6 text-center text-gray-500">
              Caricamento deleghe...
            </div>
          ) : deleghe.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nessuna delega presente per questa congregazione.
            </div>
          ) : filteredDeleghe.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              Nessuna delega trovata per la ricerca.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredDeleghe.map((delega) => (
                <div
                  key={delega.id}
                  className="relative p-5 pl-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 transition hover:bg-gray-50"
                >
                  <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
                  <div className="space-y-1">
                    <p className="font-semibold text-gray-900">
                      {delega.volontario_nome} {delega.volontario_cognome}
                    </p>
                    <div className="flex flex-wrap gap-y-1 text-sm text-gray-500">
                      <span className="mr-3 font-medium text-gray-700">
                        ID {delega.volontario_id}
                      </span>
                      <span className="mr-3 capitalize">
                        Ruolo: {delega.ruolo_delega}
                      </span>
                      {delega.congregazione_nome && (
                        <span className="mr-3">
                          Congregazione: {delega.congregazione_nome}
                          {delega.congregazione_codice
                            ? ` (Codice: ${delega.congregazione_codice})`
                            : ""}
                        </span>
                      )}
                      <span className="mr-3">
                        Congregazione di origine:{" "}
                        {delega.volontario_congregazione_origine_nome || "N/D"}
                        {delega.volontario_congregazione_origine_codice
                          ? ` (Codice: ${delega.volontario_congregazione_origine_codice})`
                          : ""}
                      </span>
                      {delega.volontario_email && (
                        <span className="mr-3 break-all">
                          {delega.volontario_email}
                        </span>
                      )}
                      {delega.volontario_telefono && (
                        <span>{delega.volontario_telefono}</span>
                      )}
                    </div>
                    {delega.coniuge_nome && (
                      <p className="text-sm text-gray-600">
                        Coniuge: {delega.coniuge_nome}{" "}
                        {delega.coniuge_cognome}
                      </p>
                    )}
                    {delega.note && (
                      <p className="text-sm text-gray-600">
                        Nota: {delega.note}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveDeleghe(delega.id)}
                    disabled={removingId === delega.id}
                    className="inline-flex items-center justify-center text-sm text-red-600 hover:text-red-700 w-full sm:w-auto"
                  >
                    <TrashIcon className="h-5 w-5 mr-1" />
                    {removingId === delega.id ? "Rimozione..." : "Rimuovi delega"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Deleghe;
